import axios from 'axios'

const ENDPOINT = '/api/internal/generator/assets'

export const PREPARE_STATUS = {
  READY: 'ready',
  PENDING: 'pending',
  CHOOSE_FILE: 'choose_file',
}

const POLL_INTERVAL_MS = 2000
// Extraction runs on the shared slow queue. Giving up lets the user click again, and that
// repeated POST re-enqueues an extraction whose retries ran out.
const POLL_TIMEOUT_MS = 3 * 60 * 1000

const csrfToken = () => {
  const meta = document.getElementsByName('csrf-token')[0]
  return meta?.getAttribute('content')
}

const wait = ms => new Promise((resolve) => { setTimeout(resolve, ms) })

// A 0-byte entry cannot hold a model (often an empty placeholder or a folder entry).
const isOpenableCandidate = candidate => candidate.fileSize !== 0

const mapCandidate = candidate => ({
  itemFileId: candidate.item_file_id,
  entryPath: candidate.entry_path,
  name: candidate.name,
  format: candidate.format,
  fileSize: candidate.file_size,
})

// A pending extraction answers without a url. The format is the one the server stored.
const fetchFileUrl = async (assetId) => {
  const response = await axios.get(`${ENDPOINT}/${assetId}/file-url`, { withCredentials: true })
  return { url: response.data?.url ?? null, format: response.data?.format ?? null }
}

export class PreparationAbandonedError extends Error {}

const ALWAYS_CONTINUE = () => true

const waitForFileUrl = async (assetId, shouldContinue) => {
  const deadline = Date.now() + POLL_TIMEOUT_MS
  while (Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await wait(POLL_INTERVAL_MS)
    if (!shouldContinue()) throw new PreparationAbandonedError()
    // eslint-disable-next-line no-await-in-loop
    const file = await fetchFileUrl(assetId)
    if (file.url) return file
  }
  throw new Error('Timed out waiting for the model file to be extracted')
}

// The internal API answers in snake_case and is not camelized, so map it here.
// An item whose models only ship inside archives answers with the files to choose from;
// the caller sends the pick back as `itemFileId` + `entryPath`. A `pending` answer means the
// picked archive entry is still being extracted, so it carries no url yet.
export const requestAcquiredModelAsset = async (modelId, { itemFileId, entryPath } = {}) => {
  const payload = itemFileId
    ? { item_id: modelId, item_file_id: itemFileId, entry_path: entryPath }
    : { item_id: modelId }
  const response = await axios.post(
    ENDPOINT,
    payload,
    {
      headers: { 'X-CSRF-Token': csrfToken() },
      withCredentials: true,
    },
  )
  const {
    status, candidates, id, url, file_size: fileSize, format,
  } = response.data

  if (status === PREPARE_STATUS.CHOOSE_FILE) {
    const openable = (candidates ?? []).map(mapCandidate).filter(isOpenableCandidate)
    if (openable.length === 0) throw new Error('The archive holds no file the viewer can open')

    return { status, candidates: openable }
  }

  return {
    status, assetId: id, url, fileSize, format,
  }
}

// Marks the shared file failed for every buyer, so it is no longer offered. Never rejects:
// the viewer already shows the failure, and a lost report only means another buyer retries.
export const reportAcquiredModelLoadFailure = async (assetId) => {
  try {
    await axios.post(
      `${ENDPOINT}/${assetId}/load-failure`,
      {},
      {
        headers: { 'X-CSRF-Token': csrfToken() },
        withCredentials: true,
      },
    )
  } catch {
    // Intentionally ignored, see above.
  }
}

// An asset prepared before the studio loaded (My Purchases creates it on click). A 404 means
// it is gone or not this user's, and the caller prepares a fresh one instead. Its ids came
// from the page URL, which a visitor can edit, so the format the server returns wins over
// the handed-over one. A format known only from the URL is flagged as unverified.
const resumePreparedAsset = async ({ assetId, format, fileSize }, shouldContinue) => {
  let file
  try {
    file = await fetchFileUrl(assetId)
  } catch (error) {
    if (error?.response?.status === 404) return null
    throw error
  }
  if (!file.url) file = await waitForFileUrl(assetId, shouldContinue)

  return {
    status: PREPARE_STATUS.READY,
    assetId,
    url: file.url,
    fileSize: fileSize ?? null,
    format: file.format ?? format,
    isFormatVerified: Boolean(file.format),
  }
}

// `shouldContinue` stops the extraction polling once the caller no longer wants the result.
// With `archiveFilesEnabled` off (the archive rollout flag), a model that only ships inside
// archives fails as it did before the rollout instead of offering the file choice.
export const prepareAcquiredModelAsset = async (
  modelId,
  archiveFile = {},
  { shouldContinue = ALWAYS_CONTINUE, preparedAsset = null, archiveFilesEnabled = true } = {},
) => {
  if (preparedAsset && archiveFilesEnabled) {
    const resumed = await resumePreparedAsset(preparedAsset, shouldContinue)
    if (resumed) return resumed
  }

  const requested = await requestAcquiredModelAsset(modelId, archiveFile)
  if (!archiveFilesEnabled && requested.status !== PREPARE_STATUS.READY) {
    throw new Error('Models inside archives cannot be opened yet')
  }
  if (requested.status !== PREPARE_STATUS.PENDING) {
    return requested.status === PREPARE_STATUS.CHOOSE_FILE
      ? requested
      : { ...requested, status: PREPARE_STATUS.READY, isFormatVerified: true }
  }

  const file = await waitForFileUrl(requested.assetId, shouldContinue)

  return {
    ...requested,
    status: PREPARE_STATUS.READY,
    url: file.url,
    isFormatVerified: true,
  }
}
